import GeoJSON from 'ol/format/GeoJSON';

import type { GeometryType } from 'cadenza/repository/model/geometry-type';
import { assertUnreachable } from 'cadenza/utils/custom-error';

import type Feature from 'ol/Feature';
import type { Geometry as GeoJSONGeometry, Feature as GeoJSONFeature } from 'geojson';
import type { ReadOptions, WriteOptions } from 'ol/format/Feature';
import type { Geometry as OLGeometry } from 'ol/geom';

export type GeoJSONGeometryType = GeoJSONGeometry['type'];

const geoJSON = new GeoJSON();
export const GEOJSON_DEFAULT_SRS_CODE = 'EPSG:4326';

export function serializeOlFeature (feature?: Feature) {
  const geometry = feature?.getGeometry();
  return serializeOlGeometry(geometry);
}

export function serializeOlGeometry (geometry?: OLGeometry) {
  return geometry ? geoJSON.writeGeometry(geometry) : undefined;
}

export function deserializeOlFeature (geoJson: string) {
  return geoJSON.readFeature(geoJson) as Feature;
}

export function olFeatureToGeoJsonGeometry (feature?: Feature, options?: WriteOptions) {
  const geometry = feature?.getGeometry();
  return olGeometryToGeoJsonGeometry(geometry, options);
}

export function olGeometryToGeoJsonGeometry (geometry?: OLGeometry, options?: WriteOptions): GeoJSONGeometry | undefined {
  return geometry ? geoJSON.writeGeometryObject(geometry, options) : undefined;
}

export function geoJsonFeatureToOlFeature (feature?: GeoJSONFeature, options?: ReadOptions): Feature<OLGeometry> | undefined {
  return feature ? geoJSON.readFeature(feature, options) as Feature : undefined;
}

export function geoJsonGeometryToOlFeature (geometry?: GeoJSONGeometry, options?: ReadOptions): Feature<OLGeometry> | undefined {
  return geometry ? geoJSON.readFeature(geometry, options) as Feature : undefined;
}

export function geoJsonGeometryToOlGeometry (geometry?: GeoJSONGeometry, options?: ReadOptions): OLGeometry | undefined {
  return geometry ? geoJSON.readGeometry(geometry, options) : undefined;
}

export function geometryTypeFromOlFeature (feature?: Feature): GeometryType {
  const geometry = feature?.getGeometry();
  if (geometry) {
    const type = geometry.getType();
    if (isGeoJsonGeometryType(type)) {
      return geometryTypeFromGeoJsonGeometryType(type);
    }
  }
  return 'UNKNOWN';
}

const GEO_JSON_GEOMETRY_TYPES = new Set<GeoJSONGeometryType>([
  'Point', 'MultiPoint',
  'Polygon', 'MultiPolygon',
  'LineString', 'MultiLineString',
  'GeometryCollection'
]);

export function isGeoJsonGeometryType (type: string): type is GeoJSONGeometryType {
  return GEO_JSON_GEOMETRY_TYPES.has(type as GeoJSONGeometryType);
}

export function geometryTypeFromGeoJsonGeometryType (type: GeoJSONGeometryType): GeometryType {
  switch (type) {
    case 'Point':
      return 'POINT';
    case 'MultiPoint':
      return 'MULTIPOINT';
    case 'LineString':
      return 'LINESTRING';
    case 'MultiLineString':
      return 'MULTILINESTRING';
    case 'Polygon':
      return 'POLYGON';
    case 'MultiPolygon':
      return 'MULTIPOLYGON';
    case 'GeometryCollection':
      return 'UNKNOWN';
    default:
      assertUnreachable(type);
  }
}
